import { useState, useCallback, useRef } from "react";
import { address as toAddress, type Transaction, type TransactionWithLifetime } from "@solana/kit";
import { solanaService } from "@/services/solana";
import { tokenService } from "@/services/token";
import { transactionRelay } from "@/services/transaction-relay";
import { useBrowserWallet, useGuardedSigner, signWithSigner } from "@/features/wallet";
import { useTransactionStore } from "@/store/transaction-store";
import { useToast } from "./useToast";
import type { TransactionResult } from "@/types/solana";

const SOLANA_CHAIN_ID = 101;

export function useSolanaSendTransaction() {
  const { publicKey } = useBrowserWallet();
  const signer = useGuardedSigner();
  const { addTransaction } = useTransactionStore();
  const { success, error: showError, info } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const sending = useRef(false);

  const send = useCallback(async (
    to: string, amount: string | number,
    token?: { mint: string; decimals: number; symbol: string },
  ): Promise<TransactionResult> => {
    if (!publicKey || !signer) throw new Error("Wallet not connected");
    if (sending.current) throw new Error("A transaction is already being prepared");
    sending.current = true;
    setIsLoading(true);
    const tempHash = `pending-${crypto.randomUUID()}`;
    const symbol = token?.symbol ?? "SOL";
    let outcome: TransactionResult | undefined;
    try {
      const sign = (tx: Transaction & TransactionWithLifetime) => signWithSigner(signer, tx);
      const result = token
        ? await tokenService.sendToken(publicKey, toAddress(to), toAddress(token.mint), amount, token.decimals, sign)
        : await solanaService.sendSOL(publicKey, toAddress(to), amount, sign);
      outcome = result;
      const saved = result.signature ? transactionRelay.read(publicKey) : null;
      // A blocked second request can return an older operation's status. Its
      // amount/recipient must come from that operation, never the new form.
      if (!result.signature || saved?.operationId === result.operationId) {
        addTransaction({ hash: result.signature || tempHash, type: "token_transfer", chainId: SOLANA_CHAIN_ID,
          from: publicKey, to: saved?.recipient ?? to, value: saved?.amount ?? String(amount),
          description: `Send ${saved?.amount ?? amount} ${saved?.asset ?? symbol}`,
          status: result.confirmed ? "confirmed" : result.status === "failed" ? "failed"
            : result.status === "not_submitted" ? "not_submitted" : "unknown", error: result.message });
      }
      if (result.confirmed) success("Transaction confirmed", { description: result.signature });
      else if (result.status === "unknown" || result.status === "submitted") {
        info("Transaction outcome not confirmed", { description: "Check the original signature before sending again." });
      } else showError(result.status === "failed" ? "Transaction failed on chain" : "Transaction not submitted", { description: result.message });
      return result;
    } catch (error: unknown) {
      if (outcome) return outcome;
      const message = error instanceof Error ? error.message : "Transaction preparation failed";
      return { signature: "", status: "not_submitted", confirmed: false, message };
    } finally {
      sending.current = false;
      setIsLoading(false);
    }
  }, [publicKey, signer, addTransaction, success, showError, info]);

  const sendSOL = useCallback((to: string, amount: string | number) => send(to, amount), [send]);
  const sendToken = useCallback((to: string, mint: string, amount: string | number, decimals: number, symbol: string) =>
    send(to, amount, { mint, decimals, symbol }), [send]);
  const recover = useCallback(async () => {
    if (!publicKey) return null;
    const saved = transactionRelay.read(publicKey);
    const result = await transactionRelay.recover(publicKey);
    if (result && saved && saved.operationId === result.operationId) {
      addTransaction({ hash: saved.signature, type: "token_transfer", chainId: SOLANA_CHAIN_ID,
        from: saved.wallet, to: saved.recipient, value: saved.amount, description: `Send ${saved.amount} ${saved.asset}`,
        status: result.confirmed ? "confirmed" : result.status === "failed" ? "failed" : "unknown" });
    }
    return result;
  }, [publicKey, addTransaction]);
  return { sendSOL, sendToken, recover, isLoading };
}
